import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import { statusLabelKey } from '@bookingdei/i18n/status-label';
import { formatPhone } from '@bookingdei/i18n/phone';
import { phonePlan } from '../theme/phone-plan';
import { api } from '../api';
import { useLanguage } from './language-context';
import { useSession } from './session-context';
import { useSettings } from './settings-context';
import { useLayer } from './layer-stack';
import { NavIcon } from './nav-icon';
import type { NavIconName } from './nav-icon';
import { StatusBadge } from './status-badge';
import { formatDateTime } from './format-datetime';
import type { BookingStatus, MerchantSearchResults } from '../mock/seeds/schema-types';

// ⌘K command palette (design M:3947-4029 + search block M:12230-12290; CB-17
// `command-palette.tsx`). One GET /merchant/search (API-126) per settled query with
// scope=all, so every scope tab can show its count; the tab only filters what is
// shown. Advanced filters map 1:1 to the op's `status`, `when` and `business_id`
// params (Business row only when the merchant holds more than one business). The
// customers group is the folded Clients page's replacement (DR-37). Enter opens the
// first result; Esc closes (top layer only, via the layer stack). API-126 is
// merchant/staff; a team member's palette drops the Services group, whose rows open the
// owner-only Services page (API-16).
type Scope = 'all' | 'bookings' | 'services' | 'customers';
type When = 'today' | 'upcoming' | 'past';

const SCOPES: readonly Scope[] = ['all', 'bookings', 'services', 'customers'];
const STATUSES: readonly BookingStatus[] = [
  'payment_verification',
  'pending',
  'confirmed',
  'completed',
  'cancelled',
];
const WHENS: readonly When[] = ['today', 'upcoming', 'past'];
const DEBOUNCE_MS = 200;

interface Row {
  id: string;
  title: string;
  sub: string;
  meta: string;
  status?: string;
  go: () => void;
}
interface Group {
  scope: Exclude<Scope, 'all'>;
  icon: NavIconName;
  rows: Row[];
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { lang, t } = useLanguage();
  const { money } = useSettings();
  const { businesses, isStaff } = useSession();
  const scopes = isStaff ? SCOPES.filter((s) => s !== 'services') : SCOPES;
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState<Scope>('all');
  const [advOpen, setAdvOpen] = useState(false);
  const [status, setStatus] = useState<BookingStatus | null>(null);
  const [when, setWhen] = useState<When | null>(null);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [results, setResults] = useState<MerchantSearchResults | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useLayer(open, { kind: 'sheet', onDismiss: onClose, containerRef: panelRef });

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const term = query.trim();
  useEffect(() => {
    if (!open || !term) {
      setResults(null);
      setBusy(false);
      return;
    }
    const controller = new AbortController();
    setBusy(true);
    const timer = setTimeout(() => {
      api
        .GET('/merchant/search', {
          signal: controller.signal,
          params: {
            query: {
              q: term.slice(0, 100),
              scope: 'all',
              ...(status ? { status } : {}),
              ...(when ? { when } : {}),
              ...(businessId ? { business_id: businessId } : {}),
            },
          },
        })
        .then((res) => {
          setResults(res);
          setFailed(false);
          setBusy(false);
        })
        .catch(() => {
          if (controller.signal.aborted) return;
          setFailed(true);
          setBusy(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, term, status, when, businessId]);

  if (!open) return null;

  const go = (to: string) => () => {
    onClose();
    navigate(to);
  };
  const nameOfBiz = (id: string) => {
    const b = businesses.find((x) => x.id === id);
    return b ? (lang === 'bn' && b.name_bn ? b.name_bn : b.name) : '';
  };

  const groups: Group[] = [
    {
      scope: 'bookings',
      icon: 'calendar',
      rows: (results?.bookings ?? []).map((b) => ({
        id: b.id,
        title: b.customer_name ?? b.code,
        sub: b.code,
        meta: formatDateTime(b.starts_at, lang),
        status: b.status,
        go: go('/bookings'),
      })),
    },
    {
      scope: 'services',
      icon: 'tag',
      rows: (results?.services ?? []).map((s) => ({
        id: s.id,
        title: lang === 'bn' && s.name_bn ? s.name_bn : s.name_en,
        sub: nameOfBiz(s.business_id),
        meta: money(s.price_minor),
        // Opens that service's editor (DC74 deep link).
        go: go(`/services?service=${encodeURIComponent(s.id)}`),
      })),
    },
    {
      scope: 'customers',
      icon: 'person',
      rows: (results?.customers ?? []).map((c) => ({
        id: `${c.name}|${c.phone ?? ''}`,
        title: c.name,
        sub: formatPhone(c.phone, phonePlan),
        meta: formatMessage(lang, 'dc70.search.bookings_count', { n: c.bookings_count ?? 0 }),
        go: go('/bookings'),
      })),
    },
  ];
  const visible = groups.filter(
    (g) => (scope === 'all' || g.scope === scope) && g.rows.length > 0 && !(isStaff && g.scope === 'services'),
  );
  const countOf = (s: Scope) =>
    s === 'all' ? null : (groups.find((g) => g.scope === s)?.rows.length ?? 0);
  const advCount = (status ? 1 : 0) + (when ? 1 : 0) + (businessId ? 1 : 0);

  const onKey = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const first = visible[0]?.rows[0];
      if (first) {
        e.preventDefault();
        first.go();
      }
    }
  };

  const scopeLabel = (s: Scope): string => {
    const label = t(`dc70.search.scope_${s}`);
    const n = countOf(s);
    return n === null || !results
      ? label
      : formatMessage(lang, 'dc70.search.scope_count', { label, n });
  };

  const chip = (label: string, on: boolean, pick: () => void, key: string) => (
    <button
      key={key}
      type="button"
      className={`palette__chip${on ? ' is-on' : ''}`}
      aria-pressed={on}
      onClick={pick}
    >
      {label}
    </button>
  );

  return (
    <div className="palette-scrim" onClick={onClose}>
      <div
        ref={panelRef}
        className="palette"
        role="dialog"
        aria-modal="true"
        aria-label={t('dc70.search.label')}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="palette__bar">
          <span className="palette__glyph">
            <NavIcon name="search" size={17} strokeWidth={2} />
          </span>
          <input
            ref={inputRef}
            className="palette__input"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKey}
            placeholder={t('dc70.top.search')}
            aria-label={t('dc70.search.label')}
            maxLength={100}
          />
          <button type="button" className="palette__esc" onClick={onClose}>
            {t('dc70.search.key_esc')}
          </button>
        </div>

        <div className="palette__scopes">
          <div className="palette__scope-row" role="tablist" aria-label={t('dc70.search.scopes')}>
            {scopes.map((s) => (
              <button
                key={s}
                type="button"
                role="tab"
                aria-selected={scope === s}
                className={`palette__scope${scope === s ? ' is-on' : ''}`}
                onClick={() => setScope(s)}
              >
                {scopeLabel(s)}
              </button>
            ))}
          </div>
          <button
            type="button"
            className={`palette__adv${advOpen || advCount ? ' is-on' : ''}`}
            aria-label={t('dc70.search.advanced')}
            title={t('dc70.search.advanced')}
            aria-expanded={advOpen}
            onClick={() => setAdvOpen((v) => !v)}
          >
            <NavIcon name="filter" size={16} strokeWidth={2} />
            {advCount > 0 && <span className="palette__adv-count">{advCount}</span>}
          </button>
        </div>

        {advOpen && (
          <div className="palette__adv-panel">
            <div className="palette__filter">
              <span className="palette__filter-name">{t('dc70.search.status')}</span>
              <div className="palette__chips">
                {chip(t('dc70.search.any'), status === null, () => setStatus(null), 'any')}
                {STATUSES.map((s) =>
                  chip(t(statusLabelKey('booking_status', s)), status === s, () => setStatus(s), s),
                )}
              </div>
            </div>
            <div className="palette__filter">
              <span className="palette__filter-name">{t('dc70.search.when')}</span>
              <div className="palette__chips">
                {chip(t('dc70.search.any'), when === null, () => setWhen(null), 'any')}
                {WHENS.map((w) =>
                  chip(t(`dc70.search.when_${w}`), when === w, () => setWhen(w), w),
                )}
              </div>
            </div>
            {businesses.length > 1 && (
              <div className="palette__filter">
                <span className="palette__filter-name">{t('dc70.search.business')}</span>
                <div className="palette__chips">
                  {chip(
                    t('dc70.search.any'),
                    businessId === null,
                    () => setBusinessId(null),
                    'any',
                  )}
                  {businesses.map((b) =>
                    chip(nameOfBiz(b.id), businessId === b.id, () => setBusinessId(b.id), b.id),
                  )}
                </div>
              </div>
            )}
            {advCount > 0 && (
              <button
                type="button"
                className="palette__reset"
                onClick={() => {
                  setStatus(null);
                  setWhen(null);
                  setBusinessId(null);
                }}
              >
                {t('dc70.search.reset')}
              </button>
            )}
          </div>
        )}

        <div className="palette__results" aria-live="polite">
          {!term ? (
            <p className="palette__hint-text">{t('dc70.search.start')}</p>
          ) : failed ? (
            <p className="palette__hint-text" role="alert">
              {t('dc70.search.error')}
            </p>
          ) : busy && !results ? (
            <p className="palette__hint-text">{t('dc70.search.searching')}</p>
          ) : visible.length === 0 ? (
            <div className="palette__empty">
              <span className="palette__empty-icon">
                <NavIcon name="search" size={20} />
              </span>
              <span className="palette__empty-title">{t('dc70.search.no_results')}</span>
              <span className="palette__empty-body">{t('dc70.search.no_results_hint')}</span>
            </div>
          ) : (
            visible.map((g) => (
              <section
                key={g.scope}
                className="palette__group"
                aria-label={t(`dc70.search.scope_${g.scope}`)}
              >
                <div className="palette__group-head">
                  <span className="palette__group-label">{t(`dc70.search.scope_${g.scope}`)}</span>
                  <span className="palette__group-count">{g.rows.length}</span>
                </div>
                {g.rows.slice(0, scope === 'all' ? 3 : 8).map((r) => (
                  <button key={r.id} type="button" className="palette__row" onClick={r.go}>
                    <span className={`palette__row-icon palette__row-icon--${g.scope}`}>
                      <NavIcon name={g.icon} size={17} strokeWidth={1.9} />
                    </span>
                    <span className="palette__row-text">
                      <span className="palette__row-title">{r.title}</span>
                      <span className="palette__row-sub">{r.sub}</span>
                    </span>
                    <span className="palette__row-end">
                      <span className="palette__row-meta">{r.meta}</span>
                      {r.status && <StatusBadge status={r.status} />}
                    </span>
                  </button>
                ))}
              </section>
            ))
          )}
        </div>

        <div className="palette__foot">
          <span className="palette__key-hint">
            <kbd>↵</kbd>
            <span>{t('dc70.search.hint_open')}</span>
          </span>
          <span className="palette__key-hint">
            <kbd>{t('dc70.search.key_esc')}</kbd>
            <span>{t('dc70.search.hint_close')}</span>
          </span>
        </div>
      </div>
    </div>
  );
}
